import { useState, useRef } from "react";
import { cn } from "@/lib/utils";
import type { FeedbackValue } from "./useFeedbackState";

const orbFills: Record<string, string> = {
  confirmed: "bg-[var(--orb-green)] border-[var(--orb-green)]",
  "not-found": "bg-[var(--orb-gray)] border-[var(--orb-gray)]",
  "not-sure": "bg-[var(--orb-amber)] border-[var(--orb-amber)]",
};

const orbSymbols: Record<string, string> = {
  confirmed: "✓",
  "not-found": "✕",
  "not-sure": "?",
};

const rippleColors: Record<string, string> = {
  confirmed: "var(--orb-green)",
  "not-found": "var(--orb-gray)",
  "not-sure": "var(--orb-amber)",
};

interface StatusOrbProps {
  feedback: FeedbackValue;
  isOnSite: boolean;
  onClick: (e: React.MouseEvent) => void;
}

export function StatusOrb({ feedback, isOnSite, onClick }: StatusOrbProps) {
  const [ripple, setRipple] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const triggerRipple = (color: string) => {
    setRipple(color);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setRipple(null), 600);
  };

  const size = isOnSite ? "w-10 h-10" : "w-8 h-8";
  const fontSize = isOnSite ? "text-base" : "text-sm";
  const hasFeedback = feedback !== null;

  const needsReview = !hasFeedback && isOnSite;

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      className={cn(
        "rounded-full border-2 flex items-center justify-center cursor-pointer shrink-0 relative transition-all duration-300 font-bold text-white active:scale-[0.92]",
        size,
        fontSize,
        hasFeedback
          ? orbFills[feedback!]
          : needsReview
          ? "border-[var(--orb-purple)] bg-transparent [animation:orb-pulse_2.5s_ease-in-out_infinite]"
          : "border-[var(--orb-border)] border-dashed bg-transparent"
      )}
    >
      {hasFeedback && orbSymbols[feedback!]}
      {ripple && (
        <span
          className="absolute inset-0 rounded-full pointer-events-none [animation:ripple-out_0.6s_ease-out_forwards]"
          style={{ borderColor: ripple, border: `2px solid ${ripple}` }}
        />
      )}
    </button>
  );
}

// Export the ripple trigger for external use
StatusOrb.triggerRipple = null as any; // placeholder
export { rippleColors };
